import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil } from "lucide-react";
import type { Json } from "@/lib/supabase/types";
import { logCodeAction } from "@/lib/codeAuditLog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase/client";
import { Modal, ModalHeader, ModalTitle } from "@/components/ui/modal";
import type {
  TOTPCode,
  TOTPCodeInsert,
  TOTPCategory,
} from "@/lib/supabase/client";
import { CategorySelect } from "@/components/CategorySelect";
import { useAuth } from "@/contexts/AuthContext";
import { canManageRevelatio, REVELATIO_TEAM_ID } from "@/lib/auth";

interface Team {
  id: string;
  name: string;
}

interface EditTOTPDialogProps {
  code: TOTPCode;
  onUpdate: () => void;
}

export function EditTOTPDialog({ code, onUpdate }: EditTOTPDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [categories, setCategories] = useState<TOTPCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<TOTPCategory | null>(
    null
  );
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const fetchData = async () => {
      try {
        const [teamsResult, categoriesResult, totpResult] = await Promise.all([
          supabase.from("teams").select("id, name").order("name"),
          supabase
            .from("totp_categories")
            .select("id, name, created_at")
            .order("name"),
          supabase
            .from("totp_codes")
            .select("team_id, category_id")
            .eq("name", code.name)
            .eq("secret", code.secret),
        ]);

        if (teamsResult.error) throw teamsResult.error;
        if (categoriesResult.error) throw categoriesResult.error;
        if (totpResult.error) throw totpResult.error;

        const includeRevelatio = user?.id
          ? await canManageRevelatio(user.id)
          : false;
        const fetchedTeams = (teamsResult.data || []).filter(
          (team) => team.id !== REVELATIO_TEAM_ID || includeRevelatio
        );
        const fetchedCategories = categoriesResult.data || [];
        const totpTeams = totpResult.data || [];

        setTeams(fetchedTeams);
        setCategories(fetchedCategories);

        const teamIds = totpTeams.map((t) => t.team_id);
        setSelectedTeams(teamIds);

        const initialCategoryIdValue =
          code.category_id ??
          (totpTeams.length > 0 ? totpTeams[0].category_id : null);

        if (initialCategoryIdValue) {
          const initialCategory =
            fetchedCategories.find(
              (category) => category.id === initialCategoryIdValue
            ) || null;
          setSelectedCategory(initialCategory);
        } else {
          setSelectedCategory(null);
        }
      } catch (error: any) {
        toast({
          variant: "destructive",
          title: "Erro ao carregar dados do TOTP",
          description: error.message,
        });
      }
    };

    fetchData();
  }, [isOpen, code.name, code.secret, code.category_id, toast, user?.id]);

  const handleCreateCategory = async (
    name: string
  ): Promise<TOTPCategory | null> => {
    try {
      const { data, error } = await supabase
        .from("totp_categories")
        .insert({ name })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        setCategories((prev) => {
          const updated = [...prev, data];
          return updated.sort((a, b) => a.name.localeCompare(b.name));
        });
        toast({
          title: "Categoria criada",
          description: `A categoria "${data.name}" foi criada com sucesso.`,
        });
      }

      return data ?? null;
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao criar categoria",
        description: error.message,
      });
      return null;
    }
  };

  const handleSubmit = async () => {
    if (selectedTeams.length === 0) {
      toast({
        variant: "destructive",
        title: "Erro de validação",
        description: "Por favor, selecione pelo menos um time",
      });
      return;
    }

    try {
      // Get current team assignments
      const { data: currentTeams, error: currentTeamsError } = await supabase
        .from("totp_codes")
        .select("team_id, category_id")
        .eq("name", code.name)
        .eq("secret", code.secret);

      if (currentTeamsError) throw currentTeamsError;

      const currentTeamIds = new Set(currentTeams?.map((t) => t.team_id) || []);
      const newTeamIds = new Set(selectedTeams);
      const nextCategoryId = selectedCategory?.id ?? null;
      const currentCategoryId =
        currentTeams && currentTeams.length > 0
          ? currentTeams[0].category_id ?? null
          : null;

      // Teams to add (in newTeamIds but not in currentTeamIds)
      const teamsToAdd = selectedTeams.filter((id) => !currentTeamIds.has(id));

      // Teams to remove (in currentTeamIds but not in newTeamIds)
      const teamsToRemove = Array.from(currentTeamIds).filter(
        (id) => !newTeamIds.has(id)
      );

      // Remove teams that should no longer have access
      if (teamsToRemove.length > 0) {
        const { error: removeError } = await supabase
          .from("totp_codes")
          .delete()
          .eq("name", code.name)
          .eq("secret", code.secret)
          .in("team_id", teamsToRemove);

        if (removeError) throw removeError;
      }

      // Add new team assignments
      if (teamsToAdd.length > 0) {
        const newAssignments: TOTPCodeInsert[] = teamsToAdd.map((teamId) => ({
          name: code.name,
          secret: code.secret,
          team_id: teamId,
          created_by: code.created_by,
          category_id: nextCategoryId,
        }));

        const { error: addError } = await supabase
          .from("totp_codes")
          .insert(newAssignments);

        if (addError) throw addError;
      }

      if (currentCategoryId !== nextCategoryId) {
        const { error: categoryUpdateError } = await supabase
          .from("totp_codes")
          .update({ category_id: nextCategoryId })
          .eq("name", code.name)
          .eq("secret", code.secret);

        if (categoryUpdateError) throw categoryUpdateError;
      }

      const teamName = (id: string) => teams.find((t) => t.id === id)?.name ?? id;
      const catName = (id: string | null) =>
        categories.find((c) => c.id === id)?.name ?? "";
      const details: Record<string, Json> = {};
      if (teamsToAdd.length) details.teams_added = teamsToAdd.map(teamName);
      if (teamsToRemove.length)
        details.teams_removed = teamsToRemove.map(teamName);
      if (currentCategoryId !== nextCategoryId)
        details.category = [catName(currentCategoryId), catName(nextCategoryId)];

      if (user && Object.keys(details).length > 0) {
        logCodeAction({
          userId: user.id,
          userEmail: user.email ?? "",
          action: "code_updated",
          codeType: "totp",
          referenceId: code.id,
          codeLabel: code.name,
          details,
        });
      }

      toast({
        title: "Sucesso",
        description: "Código TOTP atualizado com sucesso",
      });
      setIsOpen(false);
      onUpdate();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro para atualizar código TOTP",
        description: error.message,
      });
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmation !== "Deletar") {
      toast({
        variant: "destructive",
        title: "Confirmação inválida",
        description: "Digite 'Deletar' para confirmar a exclusão",
      });
      return;
    }

    try {
      // Only delete the specific TOTP code instance
      const { error } = await supabase
        .from("totp_codes")
        .delete()
        .eq("id", code.id);

      if (error) throw error;

      if (user) {
        logCodeAction({
          userId: user.id,
          userEmail: user.email ?? "",
          action: "code_deleted",
          codeType: "totp",
          referenceId: code.id,
          codeLabel: code.name,
        });
      }

      toast({
        title: "Sucesso",
        description: "Código TOTP deletado com sucesso",
      });
      setIsOpen(false);
      onUpdate();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro para deletar código TOTP",
        description: error.message,
      });
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 hover:bg-accent"
        onClick={() => setIsOpen(true)}
      >
        <Pencil className="h-4 w-4" />
      </Button>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <ModalHeader>
          <ModalTitle>Editar Código TOTP</ModalTitle>
        </ModalHeader>

        <div className="mt-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="category" className="text-foreground">
              Categoria
            </Label>
            <CategorySelect
              categories={categories}
              selectedCategoryId={selectedCategory?.id ?? null}
              onSelect={setSelectedCategory}
              onCreateCategory={handleCreateCategory}
              placeholder="Selecione ou crie uma categoria"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-foreground">Selecionar Times</Label>
            <div className="grid grid-cols-2 gap-2">
              {teams.map((team) => (
                <Button
                  key={team.id}
                  variant="outline"
                  className={`
                    border-border 
                    ${
                      selectedTeams.includes(team.id)
                        ? "bg-primary text-primary-foreground"
                        : "bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent"
                    }
                    max-[480px]:whitespace-normal
                    max-[480px]:text-xs
                  `}
                  onClick={() => {
                    setSelectedTeams((prev) =>
                      prev.includes(team.id)
                        ? prev.filter((id) => id !== team.id)
                        : [...prev, team.id]
                    );
                  }}
                >
                  {team.name}
                </Button>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Selecione um ou mais times que terão acesso a este código TOTP
            </p>
          </div>

          <div className="pt-4 border-t border-border">
            {showDelete ? (
              <div className="space-y-4">
                <p className="text-sm text-destructive">
                  Para excluir este código TOTP, digite 'Deletar' abaixo:
                </p>
                <Input
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  className="bg-secondary border-border text-foreground"
                  placeholder="Digite 'Deletar' para confirmar"
                />
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-border text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setShowDelete(false);
                      setDeleteConfirmation("");
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                    onClick={handleDelete}
                  >
                    Excluir Código TOTP
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex space-x-2">
                <Button
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={handleSubmit}
                >
                  Atualizar TOTP
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-destructive text-destructive hover:bg-destructive/10"
                  onClick={() => setShowDelete(true)}
                >
                  Excluir
                </Button>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
